import {
  ComboboxData,
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  Stack,
} from "@mantine/core"

import { CountryLabel } from "../../CountryLabel/CountryLabel"
import { MultiSelect } from "../MultiSelect/MultiSelect"
import { Select } from "../Select/Select"

import { CountryIso3166, CountryIso3166Enum } from "./CountriesSelector.types"

export type CountryMapper = Partial<{
  [Country in CountryIso3166]: {
    countryName: string
    emoji: string
    code: Country
    priority: number
  }
}>

type Props = Omit<
  React.ComponentProps<typeof Select>,
  "data" | "value" | "onChange"
> & {
  selectCountries?: CountryIso3166[]
  countriesMapper: CountryMapper
  multiple?: boolean
  value: CountryIso3166[] | CountryIso3166 | null
  onChange: (value: CountryIso3166[] | CountryIso3166 | null) => void
}

function getSelectCountriesFromCountryMapper(
  selectCountries: CountryIso3166[],
  countriesMapper: CountryMapper
) {
  return selectCountries
    .map((country) => {
      const countryData = countriesMapper[country]
      if (countryData) {
        return {
          label: countryData.countryName,
          value: countryData.code,
        }
      }
      return null
    })
    .filter(Boolean)
}

export const CountrySelectorCustomRender =
  (countriesMapper: CountryMapper) =>
  ({ option, checked }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
    if (!option) return null
    if (
      Object.values(CountryIso3166Enum).includes(option.value as CountryIso3166)
    ) {
      const countryLabel: CountryIso3166 = option.value as CountryIso3166
      return (
        <Stack
          px="var(--tfg-molecule-padding-x)"
          py="var(--tfg-molecule-padding-y)"
          data-checked={checked ? "true" : undefined}
        >
          <CountryLabel
            countryIso3166={countryLabel}
            countriesMapper={countriesMapper}
          />
        </Stack>
      )
    } else {
      return null
    }
  }

function getSortedCountryMapperCountries(countriesMapper: CountryMapper) {
  return Object.values(countriesMapper)
    .sort((c1, c2) => c2.priority - c1.priority)
    .map((country) => ({
      label: `${country.countryName}`,
      value: country.code,
    }))
}

const isMultipleValues = (
  multiple: boolean | undefined,
  value: unknown
): value is CountryIso3166[] => multiple === true && Array.isArray(value)

const isMultiSelectChange = (
  multiple: boolean | undefined,
  onChange: unknown
): onChange is (value: CountryIso3166[]) => void =>
  multiple === true && typeof onChange === "function"

export const CountrySelector = ({
  selectCountries,
  value,
  onChange,
  countriesMapper,
  multiple,
  ...props
}: Props) => {
  let countries: ComboboxData = getSortedCountryMapperCountries(countriesMapper)
  if (selectCountries) {
    countries = getSelectCountriesFromCountryMapper(
      selectCountries,
      countriesMapper
    )
  }

  if (
    isMultipleValues(multiple, value) &&
    isMultiSelectChange(multiple, onChange)
  ) {
    return (
      <MultiSelect
        data={countries}
        value={value ?? []}
        onChange={(value) => onChange(value as CountryIso3166[])}
      />
    )
  }

  return (
    <Select
      data={countries}
      value={value as CountryIso3166 | null}
      onChange={(value) => onChange(value as CountryIso3166 | null)}
      customRender={CountrySelectorCustomRender(countriesMapper)}
      errorOnTouch={false}
      placeholder={props.placeholder}
      {...props}
    />
  )
}
